import * as React from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import V2Shell from './components/V2Shell';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import {
    IconHome,
    IconActivations,
    IconProduction,
} from './components/icons';

/*
 * Platform admin chrome (ADMIN-MERGE Phase B) — /v2/admin/*. Same
 * settings grammar as /v2/org, ROUTE-gated to GlobalAdmin only —
 * classic's admin URLs were open to every authenticated user (the
 * GLOBAL_ADMIN_V1 flag only hid the menu). Everything here applies
 * across all organizations.
 */

const GLOBAL_ADMIN_ROLE = 69;

const NavIcon = ({ d }: { d: string }) => (
    <svg
        viewBox="0 0 20 20"
        className="h-[15px] w-[15px] flex-none"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d={d} />
    </svg>
);

/* ADMIN-MERGE Phase B — complete set */
const SECTIONS = [
    {
        key: 'orgs',
        label: 'Organizations',
        path: '/v2/admin',
        icon: 'M4 16.5V5.5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v11M12 8.5h3a1 1 0 0 1 1 1v7M3 16.5h14M6.5 7.5h2M6.5 10.5h2M6.5 13.5h2',
    },
    {
        key: 'licenses',
        label: 'Licenses',
        path: '/v2/admin/licenses',
        icon: 'M12.5 3.5a4 4 0 1 1-3.9 4.9L4 13v3h3l.6-.6v-1.8h1.8l1.2-1.2a4 4 0 0 1 1.9-8.9zM13.5 6.5h.01',
    },
    {
        key: 'features',
        label: 'Features',
        path: '/v2/admin/features',
        icon: 'M3.5 10a3 3 0 0 1 3-3h7a3 3 0 0 1 0 6h-7a3 3 0 0 1-3-3zM13.5 10h.01',
    },
    {
        key: 'templates',
        label: 'Templates',
        path: '/v2/admin/templates',
        icon: 'M6 3.5h8a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zM8 7h4M8 10h4M8 13h2.5',
    },
    {
        key: 'gallery-users',
        label: 'Gallery users',
        path: '/v2/admin/gallery-users',
        icon: 'M7.5 4a2.6 2.6 0 1 1 0 5.2A2.6 2.6 0 0 1 7.5 4zM2.8 15.5c.7-2.6 2.5-3.9 4.7-3.9s4 1.3 4.7 3.9M13.5 5a2.3 2.3 0 1 1 0 4.6M14.6 11.9c1.4.4 2.4 1.5 2.8 3.1',
    },
    {
        key: 'announcements',
        label: 'Announcements',
        path: '/v2/admin/announcements',
        icon: 'M4 12.5v-5l9-3.5v12l-9-3.5zM13 7.5c1.7 0 3 1.1 3 2.5s-1.3 2.5-3 2.5M5.5 12.8l1 3.7',
    },
    {
        key: 'global-settings',
        label: 'Global settings',
        path: '/v2/admin/global-settings',
        icon: 'M10 6.8a3.2 3.2 0 1 1 0 6.4 3.2 3.2 0 0 1 0-6.4zM10 2.8v2M10 15.2v2M2.8 10h2M15.2 10h2M4.9 4.9l1.4 1.4M13.7 13.7l1.4 1.4M15.1 4.9l-1.4 1.4M6.3 13.7l-1.4 1.4',
    },
    {
        key: 'system-health',
        label: 'System health',
        path: '/v2/admin/system-health',
        icon: 'M3 10h3l2-4.5 4 9 2-4.5h3',
    },
];

export default function V2AdminChrome() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { auth } = useAuth() as any;

    if (
        !auth?.roles?.some((role: number) => role === GLOBAL_ADMIN_ROLE)
    ) {
        return <Navigate to="/v2" replace />;
    }

    return (
        <V2Shell
            outlet={false}
            items={[
                {
                    key: 'home',
                    title: 'Home',
                    icon: <IconHome />,
                    onClick: () => navigate('/v2'),
                },
                {
                    key: 'jobs',
                    title: 'Jobs',
                    icon: <IconProduction />,
                    onClick: () => navigate('/v2/jobs'),
                },
                {
                    key: 'activations',
                    title: 'Activations',
                    icon: <IconActivations />,
                    onClick: () => navigate('/v2/activations'),
                },
            ]}
        >
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                <h1 className="text-center text-[23px] font-bold leading-tight tracking-[-0.02em]">
                    Platform admin
                </h1>
                <p className="mb-9 mt-1 text-center text-[13px] text-muted-foreground">
                    Applies across every organization
                </p>

                <div className="grid gap-7 md:grid-cols-[184px_minmax(0,1fr)] md:gap-11">
                    <nav
                        aria-label="Platform admin"
                        className="flex gap-1 overflow-x-auto pb-0.5 md:flex-col md:overflow-visible"
                    >
                        {SECTIONS.map((s) => {
                            const active = pathname === s.path;
                            return (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => navigate(s.path)}
                                    className={
                                        'flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-[9px] border-0 bg-transparent px-3 py-[7px] text-left font-[inherit] text-[13px] font-semibold ' +
                                        (active
                                            ? 'bg-secondary text-foreground'
                                            : 'text-muted-foreground hover:text-foreground')
                                    }
                                >
                                    <NavIcon d={s.icon} />
                                    {s.label}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="min-w-0">
                        <Outlet />
                    </div>
                </div>
            </main>
        </V2Shell>
    );
}
